import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Typography, useAlert } from "@digital-services/ui";
import { shortenOrgName } from "@ds-cps/ui";

import { CreateOrganization } from "@organization/models/CreateOrganization";
import { useAssociateAdmin } from "@organization/services/use-associate-admin";
import { useCreateOrganization } from "@organization/services/use-create-organization";
import { DisplayNameSimpleField } from "@user/components/DisplayNameSimpleField";
import { EmailSimpleField } from "@user/components/EmailSimpleField";

import CountryDropDown from "./CountryDropDown";
import { OrganizationName } from "./OrganizationName";
import OrganizationTypeDropDown from "./OrganizationTypeDropDown";

interface Props {
  onCancel: () => void;
  onSave: (organizationId: string) => void;
}

export const CreateOrganizationForm: FunctionComponent<Props> = props => {
  const { onCancel, onSave } = props;

  const [organizationName, setOrganizationName] = useState<string>("");
  const [isValidOrganizationName, setIsValidOrganizationName] =
    useState<boolean>(false);

  const [countryCode, setCountryCode] = useState<string>("");
  const [isValidCountry, setIsValidCountry] = useState<boolean>(false);

  const [organizationType, setOrganizationType] = useState<string>("");
  const [isValidOrganizationType, setIsValidOrganizationType] =
    useState<boolean>(false);

  const [displayName, setDisplayName] = useState<string>("");
  const [isValidDisplayName, setIsValidDisplayName] = useState<boolean>(false);

  const [email, setEmail] = useState<string>("");
  const [isValidEmail, setIsValidEmail] = useState<boolean>(false);

  const [isBusy, setIsBusy] = useState<boolean>(false);

  const { t: tCommon } = useTranslation();
  const { t } = useTranslation(undefined, {
    keyPrefix: "organization"
  });

  const { create } = useCreateOrganization();
  const { associate } = useAssociateAdmin();
  const { notifyError } = useAlert();

  function handleOrganizationNameChange(
    newValue: string,
    isValid: boolean
  ): void {
    setOrganizationName(newValue);
    setIsValidOrganizationName(isValid);
  }

  function handleOrganizationTypeChange(
    newValue: string,
    isValid: boolean
  ): void {
    setOrganizationType(newValue);
    setIsValidOrganizationType(isValid);
  }

  function handleCountryChange(newValue: string, isValid: boolean): void {
    setCountryCode(newValue);
    setIsValidCountry(isValid);
  }

  function handleDisplayNameChange(newValue: string, isValid: boolean): void {
    setDisplayName(newValue);
    setIsValidDisplayName(isValid);
  }

  function handleEmailChange(newValue: string, isValid: boolean): void {
    setEmail(newValue);
    setIsValidEmail(isValid);
  }

  function saveChanges(): void {
    setIsBusy(true);
    const organization: CreateOrganization = {
      countryCode: countryCode,
      name: organizationName,
      type: organizationType
    };

    const shortName = shortenOrgName(organizationName);

    create(organization)
      .then(organizationResponse => {
        setIsBusy(false);
        if (organizationResponse instanceof Error) {
          notifyError({
            header: t("create.error.message", {
              organization: organizationName
            }),
            "data-testid": "create-organization-error-alert"
          });
        } else if (organizationResponse) {
          associate(
            { emailAddress: email, displayName: displayName },
            organizationResponse.id
          )
            .then(response => {
              if (response instanceof Error) {
                notifyError({
                  header: t("associate.error.message", {
                    organization: shortName,
                    admin: displayName
                  }),
                  "data-testid": "associate-admin-error-alert"
                });
              } else {
                onSave(organizationResponse.id);
              }
              setIsBusy(false);
            })
            .catch(() => {
              notifyError({
                header: t("associate.error.message", {
                  organization: shortName,
                  admin: displayName
                }),
                "data-testid": "associate-admin-error-alert"
              });
            });
        }
      })
      .catch(() => {
        notifyError({
          header: t("create.error.message", { organization: shortName }),
          "data-testid": "create-organization-error-alert"
        });
      });
  }

  return (
    <div
      className="flex flex-col gap-10"
      data-testid="create-organization-form"
    >
      <Typography className="mb-3" variant="sectionTitle">
        {t("section.title.organization")}
      </Typography>
      <OrganizationName
        label={t("name.label")}
        value={organizationName}
        placeholder={t("name.placeholder")}
        onInputChange={handleOrganizationNameChange}
      />
      <CountryDropDown
        label={t("country.label")}
        selectedCountryCode={countryCode}
        onInputChange={handleCountryChange}
      />
      <OrganizationTypeDropDown
        label={t("type.label")}
        selectedType={organizationType}
        onInputChange={handleOrganizationTypeChange}
      />
      <Typography className="mb-3" variant="sectionTitle">
        {t("section.title.admin")}
      </Typography>

      <EmailSimpleField
        label={t("admin.email.label")}
        value={email}
        placeholder={t("admin.email.placeholder")}
        onInputChange={handleEmailChange}
      />

      <DisplayNameSimpleField
        label={t("admin.display-name.label")}
        value={displayName}
        placeholder={t("admin.display-name.placeholder")}
        onInputChange={handleDisplayNameChange}
      />

      <div className="flex flex-row-reverse gap-4">
        <Button
          data-testid="save"
          variant="primary"
          isLoading={isBusy}
          onClick={saveChanges}
          disabled={
            !isValidOrganizationName ||
            !isValidOrganizationType ||
            !isValidCountry ||
            !isValidDisplayName ||
            !isValidEmail
          }
        >
          {tCommon("button.save.label")}
        </Button>
        <Button data-testid="cancel" onClick={onCancel}>
          {tCommon("button.cancel.label")}
        </Button>
      </div>
    </div>
  );
};
